import { useState, useEffect } from 'react';
import axios from 'axios';
import { Trophy } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { PodiumMarche } from './PodiumMarche';

const TOUS = 'tous';

const Filtre = ({ value, onChange, options, libelleTous, testId }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="h-9 w-full sm:w-[170px] bg-slate-800 border-slate-700 text-slate-100" data-testid={testId}>
      <SelectValue />
    </SelectTrigger>
    <SelectContent>
      <SelectItem value={TOUS}>{libelleTous}</SelectItem>
      {options.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
    </SelectContent>
  </Select>
);

// Podium des 3 meilleures moyennes : filtres pour l'équipe, session imposée pour stagiaire et tuteur
export const PodiumStagiaires = () => {
  const [centre, setCentre] = useState(TOUS);
  const [annee, setAnnee] = useState(TOUS);
  const [data, setData] = useState(null);

  useEffect(() => {
    const params = { centre: centre === TOUS ? '' : centre, annee: annee === TOUS ? '' : annee };
    axios.get('/notes/podium', { params }).then(res => setData(res.data)).catch(() => setData({ podium: [], centres: [], annees: [], min_notes: 5, erreur: true }));
  }, [centre, annee]);

  const parRang = Object.fromEntries((data?.podium || []).map(s => [s.rang, s]));

  return (
    <section className="rounded-2xl bg-[#0F172A] p-5 sm:p-6 shadow-lg overflow-hidden" data-testid="podium-stagiaires">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-amber-400/15"><Trophy className="h-5 w-5 text-amber-400" /></div>
          <div>
            <h2 className="text-base md:text-lg font-display font-semibold text-white">Podium des stagiaires</h2>
            <p className="text-xs text-slate-400">Moyenne générale /10 — {data?.min_notes ?? 5} notes minimum</p>
          </div>
        </div>
        {data && 'session' in data ? (
          data.session && (
            <span className="self-start sm:self-auto rounded-full bg-amber-400/15 px-3 py-1 text-xs font-semibold text-amber-300" data-testid="podium-session">
              Session {data.session}
            </span>
          )
        ) : (
          <div className="flex flex-col sm:flex-row gap-2">
            <Filtre value={centre} onChange={setCentre} options={data?.centres || []} libelleTous="Tous les centres" testId="podium-filtre-centre" />
            <Filtre value={annee} onChange={setAnnee} options={data?.annees || []} libelleTous="Toutes les années" testId="podium-filtre-annee" />
          </div>
        )}
      </div>

      {!data ? (
        <div className="flex justify-center py-16" data-testid="podium-chargement">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-amber-400 border-t-transparent" />
        </div>
      ) : data.podium.length === 0 ? (
        <p className="py-10 text-center text-sm text-slate-400" data-testid="podium-vide">
          {data.erreur
            ? 'Le classement est momentanément indisponible.'
            : data.session === ''
              ? "Aucune session de formation rattachée pour l'instant."
              : `Pas encore de classement : chaque stagiaire doit avoir au moins ${data.min_notes} notes.`}
        </p>
      ) : (
        <div key={`${centre}-${annee}-${data.podium.map(s => s.user_id).join()}`} className="mt-6 flex items-end justify-center gap-2 sm:gap-6">
          {[2, 1, 3].map((rang, i) => (
            <PodiumMarche key={rang} rang={rang} stagiaire={parRang[rang]} delai={[150, 0, 300][i]} />
          ))}
        </div>
      )}
      {data?.nb_classes > 0 && (
        <p className="mt-3 text-center text-xs text-slate-500" data-testid="podium-nb-classes">{data.nb_classes} stagiaire{data.nb_classes > 1 ? 's' : ''} classé{data.nb_classes > 1 ? 's' : ''}</p>
      )}
    </section>
  );
};
